/**
 * GLSL (ES 3.00) sources. The tone mapping must match `cpu/tonemap.ts` and the offline renderer.
 */

export const MAX_ZONES = 6;
export const MAX_DECOR = 16;
/**
 * Hardware sprite slots of the door shader. Sampler budget (16 units): 7 scene passes +
 * DOOR_ZONES materials + 1 finish + MAX_SPRITES = 15.
 */
export const MAX_SPRITES = 5;
export const MAX_HINGES = 4;
/** Material slots of the door shader (wall, floor); 16 texture units are all WebGL2 guarantees. */
export const DOOR_ZONES = 2;
const NL = '\n';

export const FULLSCREEN_VERT = /* glsl */ `
precision highp float;
in vec3 position;
out vec2 vUv;
void main() {
  vUv = position.xy * 0.5 + 0.5;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

const COMMON = /* glsl */ `
precision highp float;
precision highp int;
precision highp sampler2D;

vec3 acesFilm(vec3 x) {
  return clamp((x * (2.51 * x + 0.03)) / (x * (2.43 * x + 0.59) + 0.14), 0.0, 1.0);
}
vec3 linearToSrgb(vec3 c) {
  vec3 lo = 12.92 * c;
  vec3 hi = 1.055 * pow(max(c, vec3(0.0)), vec3(1.0 / 2.4)) - 0.055;
  return mix(lo, hi, step(vec3(0.0031308), c));
}
float luma(vec3 c) { return dot(c, vec3(0.2126, 0.7152, 0.0722)); }
`;

/**
 * Shared part of the scene composite: pass decoding and zone shading. `samplers` is the number
 * of material texture units (WebGL2 guarantees only 16 units per fragment shader).
 */
const sceneCore = (samplers: number) => /* glsl */ `
uniform sampler2D uBg;
uniform sampler2D uDiff;
uniform sampler2D uGloss;
uniform sampler2D uSatin;
uniform sampler2D uScale;
uniform sampler2D uSurf;
uniform sampler2D uSurfData;
uniform vec2 uRes;
uniform float uExposure;
uniform float uGamma;
uniform float uScaleMin;
uniform float uScaleMax;
uniform int uHighlightZone;
uniform float uHighlight;

${Array.from({ length: samplers }, (_, i) => `uniform sampler2D uMat${i};`).join(NL)}
// sizeW (m), sizeH (m), cos(rotation), sin(rotation)
uniform vec4 uMatA[${MAX_ZONES}];
// gloss, specular, metallic, hasTexture
uniform vec4 uMatB[${MAX_ZONES}];
// linear tint, enabled
uniform vec4 uMatC[${MAX_ZONES}];

vec3 decodeHdr(sampler2D t, ivec2 tc, float q) {
  vec3 m = texelFetch(t, tc, 0).rgb;
  return pow(m, vec3(uGamma)) * pow(2.0, mix(uScaleMin, uScaleMax, q));
}

vec3 display(vec3 linearColor) {
  return linearToSrgb(acesFilm(linearColor * uExposure));
}

vec3 sampleMat(sampler2D tex, int i, vec2 uv, vec2 dx, vec2 dy) {
  vec4 a = uMatA[i];
  mat2 rot = mat2(a.z, a.w, -a.w, a.z);
  vec2 inv = 1.0 / a.xy;
  vec2 tuv = rot * uv * inv;
  vec2 tdx = rot * dx * inv;
  vec2 tdy = rot * dy * inv;
  // image rows run downwards while surface V runs upwards
  tuv.y = -tuv.y;
  tdx.y = -tdx.y;
  tdy.y = -tdy.y;
  return uMatB[i].w > 0.5 ? textureGrad(tex, tuv, tdx, tdy).rgb : vec3(1.0);
}

// Linear radiance of a swappable surface; returns the zone index in zone (-1 if none).
vec3 shadeSurface(int surf, vec2 p, vec3 D, vec3 G, vec3 S, out int zone) {
  vec4 r0 = texelFetch(uSurfData, ivec2(surf, 0), 0);
  vec4 r1 = texelFetch(uSurfData, ivec2(surf, 1), 0);
  vec4 r2 = texelFetch(uSurfData, ivec2(surf, 2), 0);
  zone = int(r0.w + 0.5) - 1;
  if (zone < 0 || zone >= ${samplers} || uMatC[zone].w < 0.5) {
    zone = zone >= ${samplers} ? -1 : zone;
    return D * 0.55 + S * 0.4;
  }
  vec3 P = vec3(p, 1.0);
  float uw = dot(r0.xyz, P);
  float vw = dot(r1.xyz, P);
  float w = dot(r2.xyz, P);
  vec2 uv = vec2(uw, vw) / w + vec2(r1.w, r2.w);
  float iw2 = 1.0 / (w * w);
  vec2 dx = vec2(r0.x * w - uw * r2.x, r1.x * w - vw * r2.x) * iw2 / uRes.x;
  vec2 dy = vec2(r0.y * w - uw * r2.y, r1.y * w - vw * r2.y) * iw2 / uRes.y;

  vec3 albedo = vec3(1.0);
  ${Array.from({ length: samplers }, (_, i) => `if (zone == ${i}) albedo = sampleMat(uMat${i}, ${i}, uv, dx, dy);`).join(NL)}
  albedo *= uMatC[zone].rgb;

  vec4 b = uMatB[zone];
  float peak = max(max(albedo.r, albedo.g), max(albedo.b, 1e-4));
  vec3 specTint = mix(vec3(1.0), albedo / peak, b.z);
  vec3 diffuse = albedo * D * (1.0 - 0.92 * b.z);
  vec3 refl = mix(S, G, b.x) * b.y * specTint;
  return diffuse + refl;
}

struct Composite {
  vec3 color;
  vec3 D;
  vec3 G;
  vec3 S;
  float wallWeight;
};

Composite compositeScene(ivec2 tc, vec2 p, int wallSurface) {
  vec3 q = texture(uScale, p).rgb;
  vec3 s = texelFetch(uSurf, tc, 0).rgb * 255.0;
  int idA = int(s.r + 0.5);
  int idB = int(s.g + 0.5);
  float wA = s.b / 255.0;
  vec3 bg = texelFetch(uBg, tc, 0).rgb;
  vec3 D = decodeHdr(uDiff, tc, q.r);
  vec3 G = decodeHdr(uGloss, tc, q.g);
  vec3 S = decodeHdr(uSatin, tc, q.b);

  int zA = -1;
  int zB = -1;
  vec3 cA = bg;
  vec3 cB = bg;
  if (idA > 0) cA = display(shadeSurface(idA, p, D, G, S, zA));
  if (idB > 0) {
    if (idB == idA) { cB = cA; zB = zA; }
    else cB = display(shadeSurface(idB, p, D, G, S, zB));
  }
  vec3 color = mix(cB, cA, wA);
  float hl = (zA == uHighlightZone ? wA : 0.0) + (zB == uHighlightZone ? 1.0 - wA : 0.0);
  if (uHighlightZone >= 0 && hl > 0.0) {
    color = mix(color, color * 0.78 + vec3(0.06, 0.16, 0.3), hl * uHighlight * 0.55);
  }
  float wall = (idA == wallSurface ? wA : 0.0) + (idB == wallSurface ? 1.0 - wA : 0.0);
  return Composite(color, D, G, S, wall);
}

ivec2 texelCoord() {
  ivec2 tc = ivec2(gl_FragCoord.xy);
  tc.y = int(uRes.y) - 1 - tc.y;
  return tc;
}
`;

export const SCENE_FRAG = /* glsl */ `
${COMMON}
${sceneCore(MAX_ZONES)}
out vec4 outColor;
void main() {
  ivec2 tc = texelCoord();
  vec2 p = (vec2(tc) + 0.5) / uRes;
  outColor = vec4(compositeScene(tc, p, -1).color, 1.0);
}
`;

/**
 * Door compositor: the scene composite plus a parametric door drawn on the wall plane.
 * The door reuses the wall's lighting passes (same plane), with analytic bevels, grooves,
 * panels, glass and pre-rendered hardware sprites.
 */
export const DOOR_FRAG = /* glsl */ `
${COMMON}
${sceneCore(DOOR_ZONES)}
out vec4 outColor;

uniform int uWallSurface;
uniform float uPxPerMm;
uniform vec4 uFrameRect;   // tier px: x0, y0 (top), x1, y1 (bottom)
uniform vec4 uOpeningRect;
uniform vec4 uLeafRect;
uniform float uCasingDepthMm;
uniform int uFlushFrame;
uniform vec3 uLightDir;
uniform ivec2 uRefTexel;   // wall texel near the door centre, lighting reference
vec3 lightRef;

uniform sampler2D uFinish;
uniform vec4 uFinishA;     // sizeW (m), sizeH (m), cos, sin
uniform vec4 uFinishB;     // gloss, specular, metallic, hasTexture
uniform vec3 uFinishTint;

uniform int uDecorCount;
uniform vec4 uDecorRect[${MAX_DECOR}];  // px relative to leaf top-left: x0, y0, x1, y1
uniform vec4 uDecorInfo[${MAX_DECOR}];  // kind (0 groove, 1 panel, 2 glass), bevel px, raised, frame px
uniform vec4 uGlass;                    // tint rgb (linear), opacity
uniform float uGlassFrosted;

uniform int uHingeCount;
uniform float uHingeY[${MAX_HINGES}];   // tier px (centre)
uniform float uHingeX;                  // tier px (centre)
uniform vec3 uHwColor;                  // linear

uniform int uSpriteCount;
${Array.from({ length: MAX_SPRITES }, (_, i) => `uniform sampler2D uSprite${i};`).join(NL)}
uniform vec4 uSpriteRect[${MAX_SPRITES}];   // tier px
uniform vec4 uSpriteInfo[${MAX_SPRITES}];   // mirror, depth px, 0, 0

float boxSdf(vec2 p, vec4 r) {
  vec2 c = (r.xy + r.zw) * 0.5;
  vec2 h = (r.zw - r.xy) * 0.5;
  vec2 d = abs(p - c) - h;
  return length(max(d, 0.0)) + min(max(d.x, d.y), 0.0);
}

// Outward gradient of boxSdf (analytic; screen-space derivatives are undefined in branches).
vec2 boxGrad(vec2 p, vec4 r) {
  vec2 c = (r.xy + r.zw) * 0.5;
  vec2 h = (r.zw - r.xy) * 0.5;
  vec2 q = p - c;
  vec2 d = abs(q) - h;
  vec2 s = vec2(q.x < 0.0 ? -1.0 : 1.0, q.y < 0.0 ? -1.0 : 1.0);
  if (max(d.x, d.y) > 0.0) {
    vec2 m = max(d, 0.0);
    return s * m / max(length(m), 1e-6);
  }
  return d.x > d.y ? vec2(s.x, 0.0) : vec2(0.0, s.y);
}

vec3 finishAlbedo(vec2 mm, bool horizontal) {
  vec2 uv = horizontal ? vec2(mm.y, -mm.x) : mm;
  uv *= 0.001;
  mat2 rot = mat2(uFinishA.z, uFinishA.w, -uFinishA.w, uFinishA.z);
  vec2 tuv = rot * uv / uFinishA.xy;
  tuv.y = -tuv.y;
  vec3 a = uFinishB.w > 0.5 ? texture(uFinish, tuv).rgb : vec3(1.0);
  return a * uFinishTint;
}

// Diffuse shading factor for a tilted normal relative to the flat wall.
float tiltShade(vec3 n) {
  vec3 l = normalize(uLightDir);
  float flat_ = 0.55 + 0.45 * max(l.z, 0.0);
  float tilted = 0.55 + 0.45 * max(dot(normalize(n), l), 0.0);
  return tilted / flat_;
}

vec3 shadeFinish(vec3 albedo, vec3 n, float ao, vec3 D, vec3 G, vec3 S) {
  vec4 b = uFinishB;
  float peak = max(max(albedo.r, albedo.g), max(albedo.b, 1e-4));
  vec3 specTint = mix(vec3(1.0), albedo / peak, b.z);
  float facing = clamp(normalize(n).z, 0.0, 1.0);
  vec3 diffuse = albedo * D * tiltShade(n) * ao * (1.0 - 0.92 * b.z);
  vec3 refl = mix(S, G, b.x) * b.y * specTint * mix(0.6, 1.0, facing) * mix(0.5, 1.0, ao);
  return diffuse + refl;
}

vec4 sampleSprite(int i, vec2 uv, float bias) {
  ${Array.from({ length: MAX_SPRITES - 1 }, (_, i) => `if (i == ${i}) return texture(uSprite${i}, uv, bias);`).join(NL)}
  return texture(uSprite${MAX_SPRITES - 1}, uv, bias);
}

void main() {
  ivec2 tc = texelCoord();
  vec2 p = (vec2(tc) + 0.5) / uRes;
  Composite scene = compositeScene(tc, p, uWallSurface);
  vec3 color = scene.color;
  lightRef = decodeHdr(uDiff, uRefTexel, texture(uScale, (vec2(uRefTexel) + 0.5) / uRes).r);
  vec2 px = vec2(tc) + 0.5;
  vec3 D = scene.D;
  vec3 G = scene.G;
  vec3 S = scene.S;

  // soft contact shadow of the frame on the wall
  vec2 shadowShift = -uLightDir.xy / max(uLightDir.z, 0.35) * vec2(1.0, -1.0) * uCasingDepthMm * uPxPerMm;
  float frameDist = boxSdf(px - shadowShift, uFrameRect);
  float soft = max(2.0, uCasingDepthMm * uPxPerMm * 1.5);
  if (uFlushFrame == 0 && frameDist > 0.0 && scene.wallWeight > 0.0) {
    float sh = 1.0 - smoothstep(0.0, soft, frameDist);
    color *= 1.0 - 0.35 * sh * scene.wallWeight;
  }

  float dFrame = boxSdf(px, uFrameRect);
  if (dFrame < 0.5 && scene.wallWeight > 0.0) {
    float coverage = clamp(0.5 - dFrame, 0.0, 1.0) * scene.wallWeight;
    vec3 doorLinear;
    float dOpening = boxSdf(px, uOpeningRect);
    float dLeaf = boxSdf(px, uLeafRect);
    if (dOpening > 0.0) {
      // casing (mitred jambs and head)
      vec2 rel = px - uFrameRect.xy;
      vec2 fromRight = uFrameRect.zw - px;
      float casingPx = uOpeningRect.x - uFrameRect.x;
      float side = min(rel.x, fromRight.x);
      float top = rel.y;
      bool head = top < side && top < casingPx;
      vec2 mm = vec2(px.x - uFrameRect.x, uFrameRect.w - px.y) / uPxPerMm;
      vec3 albedo = finishAlbedo(mm, head);
      float edgeOuter = -dFrame;
      float edgeInner = dOpening;
      float bevel = 3.0 * uPxPerMm + 0.5;
      vec3 n = vec3(0.0, 0.0, 1.0);
      if (edgeOuter < bevel) {
        vec2 g = boxGrad(px, uFrameRect);
        n = normalize(vec3(g.x, -g.y, 1.2));
      } else if (edgeInner < bevel) {
        vec2 g = boxGrad(px, uOpeningRect);
        n = normalize(vec3(-g.x, g.y, 1.2));
      }
      float ao = uFlushFrame == 1 ? 0.8 : 1.0;
      doorLinear = shadeFinish(albedo, n, ao, D, G, S);
      if (uFlushFrame == 1) doorLinear = D * vec3(0.72) * 0.9;
    } else if (dLeaf > 0.0) {
      // reveal gap between leaf and frame
      doorLinear = D * 0.04;
    } else {
      vec2 local = px - uLeafRect.xy;
      vec2 mm = vec2(local.x, uLeafRect.w - px.y) / uPxPerMm;
      vec3 albedo = finishAlbedo(mm, false);
      vec3 n = vec3(0.0, 0.0, 1.0);
      float ao = 1.0;
      float edge = -dLeaf;
      float leafBevel = 1.6 * uPxPerMm + 0.5;
      if (edge < leafBevel) {
        vec2 g = boxGrad(px, uLeafRect);
        n = normalize(vec3(g.x, -g.y, 1.5));
        ao = mix(0.75, 1.0, edge / leafBevel);
      }
      bool glass = false;
      vec3 glassColor = vec3(0.0);
      for (int i = 0; i < ${MAX_DECOR}; i++) {
        if (i >= uDecorCount) break;
        vec4 r = uDecorRect[i];
        vec4 info = uDecorInfo[i];
        float d = boxSdf(local, r);
        int kind = int(info.x + 0.5);
        if (kind == 0) {
          // groove: dark recessed channel with lit/shaded walls
          float half_ = min(r.z - r.x, r.w - r.y) * 0.5;
          if (d < 0.5) {
            float inside = clamp(0.5 - d, 0.0, 1.0);
            vec2 c = (r.xy + r.zw) * 0.5;
            vec2 off = (local - c);
            bool horizontal = (r.z - r.x) > (r.w - r.y);
            float t = horizontal ? off.y / max(half_, 0.5) : off.x / max(half_, 0.5);
            vec3 gn = horizontal ? vec3(0.0, t, 0.6) : vec3(-t, 0.0, 0.6);
            n = mix(n, normalize(gn), inside);
            ao = mix(ao, 0.35 + 0.25 * abs(t), inside);
          }
        } else if (kind == 1) {
          // panel: bevel ring inside the rectangle
          float bevelPx = max(info.y, 1.0);
          if (d < 0.5 && d > -bevelPx) {
            vec2 g = boxGrad(local, r);
            // raised panels rise inwards (normal tilts outwards); recessed ones sink inwards
            float s = info.z > 0.5 ? 1.0 : -1.0;
            n = normalize(vec3(g.x * s, -g.y * s, 1.4));
            ao *= info.z > 0.5 ? 1.0 : mix(0.7, 1.0, clamp(-d / bevelPx, 0.0, 1.0));
          } else if (d <= -bevelPx && info.z < 0.5) {
            ao *= 0.94;
          }
          if (abs(d) < 1.0) ao *= 0.7;
        } else {
          // glass insert with an optional frame
          if (d < 0.5) {
            float cover = clamp(0.5 - d, 0.0, 1.0);
            float framePx = info.w;
            if (framePx > 0.5 && d > -framePx) {
              ao *= 0.85;
            } else {
              glass = true;
              vec3 tint = uGlass.rgb;
              float frosted = uGlassFrosted;
              // room light seen through the glass, at most as bright as a white surface in
              // the same light (so clear glass never outshines the leaf); frosted glass
              // diffuses it into a flatter, slightly brighter field
              float grad = 0.75 + 0.25 * (1.0 - (local.y - r.y) / max(r.w - r.y, 1.0));
              grad = mix(grad, 0.9, frosted);
              vec3 behind = lightRef * grad * mix(0.85, 0.95, frosted);
              // the glass body filters it (Beer-Lambert): denser tints absorb more
              vec3 transmit = pow(max(tint, vec3(1e-3)), vec3(mix(0.4, 1.0, uGlass.a)));
              glassColor = mix(glassColor, behind * transmit + G * 0.08, cover);
            }
          }
        }
      }
      doorLinear = glass ? glassColor : shadeFinish(albedo, n, ao, D, G, S);
    }
    // hinge knuckles on the viewed face (outward opening), centred on the leaf/frame joint
    if (dOpening <= 0.0) {
      for (int i = 0; i < ${MAX_HINGES}; i++) {
        if (i >= uHingeCount) break;
        vec2 hp = px - vec2(uHingeX, uHingeY[i]);
        vec2 hs = vec2(7.0, 50.0) * uPxPerMm;
        if (abs(hp.x) < hs.x && abs(hp.y) < hs.y) {
          float t = hp.x / hs.x;
          vec3 metal = uHwColor * (0.35 + 0.65 * (1.0 - t * t)) + vec3(0.25) * pow(1.0 - abs(t + 0.35), 8.0);
          doorLinear = metal * luma(D) * 1.6;
        }
      }
    }
    vec3 doorDisplay = display(doorLinear);

    // hardware sprites (premultiplied, display-referred) with soft drop shadows
    for (int i = 0; i < ${MAX_SPRITES}; i++) {
      if (i >= uSpriteCount) break;
      vec4 r = uSpriteRect[i];
      vec4 info = uSpriteInfo[i];
      vec2 size = r.zw - r.xy;
      vec2 shift = -uLightDir.xy / max(uLightDir.z, 0.35) * vec2(1.0, -1.0) * info.y;
      vec2 suv = (px - shift - r.xy) / size;
      if (info.x > 0.5) suv.x = 1.0 - suv.x;
      if (all(greaterThanEqual(suv, vec2(-0.1))) && all(lessThanEqual(suv, vec2(1.1)))) {
        float a = sampleSprite(i, clamp(suv, 0.0, 1.0), 2.5).a;
        doorDisplay *= 1.0 - 0.38 * a;
      }
      vec2 uv = (px - r.xy) / size;
      if (info.x > 0.5) uv.x = 1.0 - uv.x;
      if (all(greaterThanEqual(uv, vec2(0.0))) && all(lessThanEqual(uv, vec2(1.0)))) {
        vec4 spr = sampleSprite(i, uv, 0.0);
        float light = clamp(sqrt(luma(D) / max(luma(lightRef), 1e-4)), 0.7, 1.25);
        doorDisplay = spr.rgb * light + doorDisplay * (1.0 - spr.a);
      }
    }
    color = mix(color, doorDisplay, coverage);
  }
  outColor = vec4(color, 1.0);
}
`;

export const DISPLAY_FRAG = /* glsl */ `
${COMMON}
in vec2 vUv;
out vec4 outColor;
uniform sampler2D uA;
uniform sampler2D uB;
uniform float uMix;
uniform vec4 uView;       // visible scene region: x0, y0, x1, y1 (normalised, y down)
uniform vec2 uCanvas;     // canvas pixels
uniform vec2 uScene;      // scene aspect reference (tier pixels)
uniform vec3 uBackground; // display-referred
uniform float uFit;       // 0 = contain, 1 = cover

void main() {
  vec2 frag = vec2(vUv.x, 1.0 - vUv.y) * uCanvas;
  float scale = mix(min(uCanvas.x / uScene.x, uCanvas.y / uScene.y), max(uCanvas.x / uScene.x, uCanvas.y / uScene.y), uFit);
  vec2 size = uScene * scale;
  vec2 origin = (uCanvas - size) * 0.5;
  vec2 st = (frag - origin) / size;
  if (any(lessThan(st, vec2(0.0))) || any(greaterThan(st, vec2(1.0)))) {
    outColor = vec4(uBackground, 1.0);
    return;
  }
  vec2 sceneUv = mix(uView.xy, uView.zw, st);
  vec2 texUv = vec2(sceneUv.x, 1.0 - sceneUv.y);
  vec3 a = texture(uA, texUv).rgb;
  vec3 b = uMix > 0.0 ? texture(uB, texUv).rgb : a;
  outColor = vec4(mix(a, b, uMix), 1.0);
}
`;
